import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const LanguageContext = createContext(null);

const SUPPORTED_LANGUAGES = ['en', 'ro', 'es', 'fr', 'de'];

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  const langCode = browserLang.split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(langCode) ? langCode : 'en';
};

const getInitialLanguage = () => {
  const stored = localStorage.getItem('language');
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
    return stored;
  }

  const userData = localStorage.getItem('user');
  if (userData) {
    try {
      const user = JSON.parse(userData);
      if (user.language && SUPPORTED_LANGUAGES.includes(user.language)) {
        localStorage.setItem('language', user.language);
        return user.language;
      }
    } catch (error) {
      console.error('Failed to parse user data', error);
    }
  }

  const detected = detectBrowserLanguage();
  localStorage.setItem('language', detected);
  return detected;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  const changeLanguage = async (newLang) => {
    if (!SUPPORTED_LANGUAGES.includes(newLang)) return;
    if (newLang === language) return;
    if (isChangingLanguage) return;

    setIsChangingLanguage(true);

    try {
      setLanguageState(newLang);
      localStorage.setItem('language', newLang);

      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          user.language = newLang;
          localStorage.setItem('user', JSON.stringify(user));
        } catch (error) {
          console.error('Failed to update user language locally', error);
        }
      }

      const token = localStorage.getItem('token');
      if (token) {
        try {
          await api.patch('/auth/language', { language: newLang });
        } catch (error) {
          console.error('Failed to update language in backend', error);
        }
      }

    } catch (error) {
      console.error('Unexpected language change error', error);
      setIsChangingLanguage(false);
    }
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        changeLanguage,
        setLanguage: changeLanguage,
        isChangingLanguage,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
